import { ArrowLeft, Copy, KeyRound, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsUtilisateur, useAgents, useServicesDirection, useStructures, useUtilisateur, type HabilitationSaisie } from '@/api/hooks';
import { LIBELLE_CATEGORIE, LIBELLE_PROFIL, LIBELLE_REGIME, PROFILS, PROFILS_TRANSVERSAUX, type ActionCompte, type Profil } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, dateHeureFr } from '@/lib/utils';
import { DialogueUtilisateur } from './Utilisateurs';

const LIBELLE_ACTION: Record<ActionCompte, string> = {
  CREER: 'Créer le compte local',
  REINITIALISER_MOT_DE_PASSE: 'Réinitialiser le mot de passe',
  REINITIALISER_TOTP: 'Réinitialiser la double authentification',
  DEVERROUILLER: 'Déverrouiller',
  DESACTIVER: 'Suspendre le compte',
  ACTIVER: 'Rétablir le compte',
};

export function UtilisateurDetail() {
  const { id } = useParams();
  const session = useSession();
  const moi = session!.utilisateur;
  const admin = moi.profils.includes('ADMINISTRATEUR');
  const { data: u, isLoading, error } = useUtilisateur(id);
  const actions = useActionsUtilisateur();
  const structures = useStructures();
  const agents = useAgents();
  const [modification, setModification] = useState(false);
  const [habilitation, setHabilitation] = useState<HabilitationSaisie | null>(null);
  const [retrait, setRetrait] = useState<string | null>(null);
  const [desactivation, setDesactivation] = useState(false);
  const [motDePasse, setMotDePasse] = useState<string | null>(null);

  // Appelé avant tout retour anticipé : l'ordre des hooks doit être stable entre le chargement et l'affichage.
  const servicesDuPerimetre = useServicesDirection(habilitation?.profil === 'CHEF_SERVICE' && habilitation.structureCode ? habilitation.structureCode : undefined);
  if (isLoading) return <Chargement />;
  if (error || !u) return <ErreurBloc erreur={error} />;

  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const compte = async (action: ActionCompte) => {
    try {
      const r = await actions.compteLocal.mutateAsync({ id: u.id, action });
      if (r.motDePasseInitial) setMotDePasse(r.motDePasseInitial);
      else toast.success(`${LIBELLE_ACTION[action]} : fait.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const ajouterHabilitation = async () => {
    if (!habilitation) return;
    const corps: HabilitationSaisie = { profil: habilitation.profil, structureCode: habilitation.structureCode || undefined, serviceCode: habilitation.profil === 'CHEF_SERVICE' ? habilitation.serviceCode || undefined : undefined, titulaireId: habilitation.titulaireId || undefined, debut: habilitation.debut || undefined, fin: habilitation.fin || undefined };
    try {
      await actions.ajouterHabilitation.mutateAsync({ id: u.id, corps });
      toast.success('Habilitation ajoutée.');
      setHabilitation(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ajout impossible.');
    }
  };
  const transversal = habilitation ? PROFILS_TRANSVERSAUX.includes(habilitation.profil) : false;
  const actives = u.habilitations.filter((h) => h.actif);
  const retirees = u.habilitations.filter((h) => !h.actif);

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/administration/utilisateurs" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Utilisateurs
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {u.prenom} {u.nom} {!u.actif && <Badge variant="destructive">désactivé</Badge>}
          </span>
        }
        sousTitre={`${u.fonction} · ${u.structure?.libelle ?? '—'} · ${u.matricule}`}
        actions={
          admin && (
            <>
              <Button variant="outline" onClick={() => setModification(true)}>
                <Pencil className="size-4" /> Modifier la fiche
              </Button>
              {u.actif ? (
                <Button variant="outline" onClick={() => setDesactivation(true)} disabled={u.id === moi.agentId}>
                  Désactiver
                </Button>
              ) : (
                <Button onClick={() => executer(() => actions.reactiver.mutateAsync(u.id), 'Agent réactivé.')}>Réactiver</Button>
              )}
            </>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Fiche</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">Adresse</span><div>{u.email}</div></div>
              <div><span className="text-muted-foreground">Catégorie</span><div>{u.categorie} · {LIBELLE_CATEGORIE[u.categorie]}</div></div>
              <div><span className="text-muted-foreground">Base</span><div>{u.base === 'SIEGE' ? 'Siège' : 'Coordination provinciale'}{u.province ? ` · ${u.province}` : ''}</div></div>
              <div><span className="text-muted-foreground">Structure</span><div>{u.structure ? `${u.structure.code} · ${u.structure.libelle}` : '—'}</div></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Fiche RH
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/rh/agents/${u.id}`}>Ouvrir la fiche RH</Link>
                </Button>
              </CardTitle>
              <CardDescription>Tenue par les Ressources humaines (démarches RH) : grade, supérieur hiérarchique, dates, régime de congé, éligibilité.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">Grade</span><div>{u.rh.grade ?? '—'}</div></div>
              <div><span className="text-muted-foreground">Supérieur hiérarchique</span><div>{u.rh.superieur?.nom ?? '—'}</div></div>
              <div><span className="text-muted-foreground">Date d'engagement</span><div>{u.rh.dateEngagement ? dateFr(u.rh.dateEngagement) : '—'}{u.rh.dateAnciennete ? ` · ancienneté reconnue depuis le ${dateFr(u.rh.dateAnciennete)}` : ''}</div></div>
              <div><span className="text-muted-foreground">Régime de congé</span><div>{u.rh.regimeConge ? `${LIBELLE_REGIME[u.rh.regimeConge]} · confirmé le ${dateFr(u.rh.regimeConfirmeLe ?? '')}` : 'À confirmer par les RH'}{u.rh.eligibleRh ? '' : ' · non éligible aux démarches RH'}</div></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Habilitations
                {admin && u.actif && (
                  <Button size="sm" variant="outline" onClick={() => setHabilitation({ profil: 'EMETTEUR', structureCode: u.structure?.code ?? '' })}>
                    <Plus className="size-4" /> Ajouter
                  </Button>
                )}
              </CardTitle>
              <CardDescription>Un profil sur un périmètre (une structure ou tout le Fonds), éventuellement par intérim d’un titulaire et pour une période.</CardDescription>
            </CardHeader>
            <CardContent>
              {actives.length === 0 ? (
                <div className="text-sm text-muted-foreground">Aucune habilitation active : l’agent peut se connecter mais n’a accès à aucun dossier.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Profil</TableHead>
                      <TableHead>Périmètre</TableHead>
                      <TableHead className="hidden sm:table-cell">Intérim de</TableHead>
                      <TableHead className="hidden sm:table-cell">Période</TableHead>
                      {admin && <TableHead />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actives.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="font-medium">{h.libelle}</TableCell>
                        <TableCell>{h.structure ? `${h.structure.code} · ${h.structure.libelle}${h.service ? ` › ${h.service.code} · ${h.service.libelle}` : ''}` : 'Tout le Fonds'}</TableCell>
                        <TableCell className="hidden sm:table-cell">{h.titulaire?.nom ?? '—'}</TableCell>
                        <TableCell className="hidden sm:table-cell">{h.debut || h.fin ? `${h.debut ? dateFr(h.debut) : '…'} → ${h.fin ? dateFr(h.fin) : '…'}` : 'permanente'}</TableCell>
                        {admin && (
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => setRetrait(h.id)} disabled={u.id === moi.agentId && h.profil === 'ADMINISTRATEUR'}>
                              Retirer
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {retirees.length > 0 && (
                <details className="mt-3 text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Habilitations retirées ({retirees.length})</summary>
                  <ul className="mt-1 list-disc pl-5">
                    {retirees.map((h) => (
                      <li key={h.id}>
                        {h.libelle} · {h.structure ? h.structure.code : 'tout le Fonds'}{h.service ? `/${h.service.code}` : ''} · retirée le {h.fin ? dateFr(h.fin) : '—'}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="size-4" /> Accès
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Microsoft 365</span>
                <div>{u.microsoft ? 'Compte rattaché (connexion déjà effectuée)' : 'Connexion possible avec l’adresse de l’agent si elle existe dans le locataire'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Compte local</span>
                {u.compteLocal ? (
                  <div className="flex flex-wrap gap-1 pt-1">
                    <Badge variant={u.compteLocal.actif ? 'secondary' : 'destructive'}>{u.compteLocal.actif ? 'actif' : 'suspendu'}</Badge>
                    {u.compteLocal.verrouille && <Badge variant="destructive">verrouillé</Badge>}
                    <Badge variant="outline">{u.compteLocal.totp ? 'double authentification active' : 'sans double authentification'}</Badge>
                    {u.compteLocal.doitChangerMotDePasse && <Badge variant="outline">mot de passe initial à changer</Badge>}
                    <div className="w-full text-xs text-muted-foreground">Dernier accès : {dateHeureFr(u.compteLocal.dernierAcces)}</div>
                  </div>
                ) : (
                  <div>Aucun</div>
                )}
              </div>
              {admin && u.actif && (
                <div className="flex flex-col gap-2 pt-1">
                  {!u.compteLocal && (
                    <Button size="sm" onClick={() => compte('CREER')} disabled={actions.compteLocal.isPending}>
                      {LIBELLE_ACTION.CREER}
                    </Button>
                  )}
                  {u.compteLocal && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => compte('REINITIALISER_MOT_DE_PASSE')} disabled={actions.compteLocal.isPending}>
                        {LIBELLE_ACTION.REINITIALISER_MOT_DE_PASSE}
                      </Button>
                      {u.compteLocal.totp && (
                        <Button size="sm" variant="outline" onClick={() => compte('REINITIALISER_TOTP')} disabled={actions.compteLocal.isPending}>
                          {LIBELLE_ACTION.REINITIALISER_TOTP}
                        </Button>
                      )}
                      {u.compteLocal.verrouille && (
                        <Button size="sm" variant="outline" onClick={() => compte('DEVERROUILLER')} disabled={actions.compteLocal.isPending}>
                          {LIBELLE_ACTION.DEVERROUILLER}
                        </Button>
                      )}
                      {u.compteLocal.actif ? (
                        <Button size="sm" variant="outline" onClick={() => compte('DESACTIVER')} disabled={actions.compteLocal.isPending || u.id === moi.agentId}>
                          {LIBELLE_ACTION.DESACTIVER}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => compte('ACTIVER')} disabled={actions.compteLocal.isPending}>
                          {LIBELLE_ACTION.ACTIVER}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {modification && <DialogueUtilisateur ouvert={modification} onOpenChange={setModification} existant={u} />}

      <ConfirmationDialog
        ouvert={desactivation}
        onOpenChange={setDesactivation}
        titre={`Désactiver ${u.prenom} ${u.nom}`}
        description="La connexion est refusée et le compte local suspendu ; les habilitations et l’historique sont conservés."
        libelle="Désactiver"
        onConfirmer={(m) => executer(() => actions.desactiver.mutateAsync({ id: u.id, motif: m || undefined }), 'Agent désactivé.').then(() => setDesactivation(false))}
        chargement={actions.desactiver.isPending}
        champ={{ label: 'Motif (facultatif)' }}
      />
      <ConfirmationDialog
        ouvert={retrait !== null}
        onOpenChange={(o) => !o && setRetrait(null)}
        titre="Retirer l’habilitation"
        description="L’habilitation est conservée dans l’historique avec sa date de fin."
        libelle="Retirer"
        onConfirmer={() => executer(() => actions.retirerHabilitation.mutateAsync({ id: u.id, habilitationId: retrait! }), 'Habilitation retirée.').then(() => setRetrait(null))}
        chargement={actions.retirerHabilitation.isPending}
      />

      <Dialog open={habilitation !== null} onOpenChange={(o) => !o && setHabilitation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une habilitation</DialogTitle>
            <DialogDescription>Les profils transversaux (DF, DGA Finances, DG, trésorerie…) s’exercent sur tout le Fonds. Un intérim porte le nom du titulaire et une date de fin.</DialogDescription>
          </DialogHeader>
          {habilitation && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Profil</Label>
                <SelectNatif value={habilitation.profil} onChange={(e) => setHabilitation({ ...habilitation, profil: e.target.value as Profil })}>
                  {PROFILS.map((p) => (
                    <option key={p} value={p}>
                      {LIBELLE_PROFIL[p]}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              {!transversal && (
                <div className="space-y-1.5">
                  <Label>Structure du périmètre</Label>
                  <SelectNatif value={habilitation.structureCode ?? ''} onChange={(e) => setHabilitation({ ...habilitation, structureCode: e.target.value })}>
                    <option value="">Tout le Fonds</option>
                    {(structures.data ?? []).map((s) => (
                      <option key={s.id} value={s.code}>
                        {s.code} · {s.libelle}
                      </option>
                    ))}
                  </SelectNatif>
                </div>
              )}
              {habilitation.profil === 'CHEF_SERVICE' && (
                <div className="space-y-1.5">
                  <Label>Service du périmètre</Label>
                  <SelectNatif value={habilitation.serviceCode ?? ''} onChange={(e) => setHabilitation({ ...habilitation, serviceCode: e.target.value })}>
                    <option value="">Choisir un service…</option>
                    {(servicesDuPerimetre.data ?? []).filter((s) => s.actif).map((s) => (
                      <option key={s.id} value={s.code}>
                        {s.code} · {s.libelle}
                      </option>
                    ))}
                  </SelectNatif>
                  {habilitation.structureCode && servicesDuPerimetre.data?.length === 0 && <p className="text-xs text-muted-foreground">Aucun service dans cette direction : créez-le dans Services des directions.</p>}
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Intérim de (facultatif)</Label>
                <SelectNatif value={habilitation.titulaireId ?? ''} onChange={(e) => setHabilitation({ ...habilitation, titulaireId: e.target.value })}>
                  <option value="">Aucun : habilitation en propre</option>
                  {(agents.data ?? []).filter((a) => a.id !== u.id).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.prenom} {a.nom} · {a.fonction}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Début</Label>
                  <Input type="date" value={habilitation.debut ?? ''} onChange={(e) => setHabilitation({ ...habilitation, debut: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Fin {habilitation.titulaireId ? '(obligatoire pour un intérim)' : ''}</Label>
                  <Input type="date" value={habilitation.fin ?? ''} onChange={(e) => setHabilitation({ ...habilitation, fin: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHabilitation(null)}>Fermer</Button>
            <Button onClick={ajouterHabilitation} disabled={actions.ajouterHabilitation.isPending}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={motDePasse !== null} onOpenChange={(o) => !o && setMotDePasse(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mot de passe initial</DialogTitle>
            <DialogDescription>Remettez-le à l’agent par un canal sûr : il n’est affiché qu’une seule fois et devra être changé à la première connexion.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-base">{motDePasse}</code>
            <Button variant="outline" size="icon" aria-label="Copier" onClick={() => navigator.clipboard?.writeText(motDePasse ?? '').then(() => toast.success('Copié.'))}>
              <Copy className="size-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setMotDePasse(null)}>J’ai noté le mot de passe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
